import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { C, FONT, R, SP } from '../../theme/tokens';

type IonName = React.ComponentProps<typeof Ionicons>['name'];

type CategoryCardProps = {
  title: string;
  description?: string;
  /** Ionicon name (preferred for consistency). */
  icon?: IonName;
  /** Or an emoji character (used by existing overview data). */
  emoji?: string;
  onPress?: () => void;
  /** Children render below the title/description block (e.g. a sub-item list). */
  children?: React.ReactNode;
  /** Call-to-action text rendered at the bottom (e.g. "View Calculations →"). */
  cta?: string;
};

/**
 * Uniform category tile used on Home + overview + category screens.
 * Monochrome by design — accent reserved for CTAs/active states only.
 */
export function CategoryCard({ title, description, icon, emoji, onPress, children, cta }: CategoryCardProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
    >
      <View style={styles.headerRow}>
        {icon ? (
          <View style={styles.iconBox}>
            <Ionicons name={icon} size={20} color={C.ink2} />
          </View>
        ) : emoji ? (
          <View style={styles.emojiBox}>
            <Text style={styles.emoji}>{emoji}</Text>
          </View>
        ) : null}
        <View style={styles.titleCol}>
          <Text style={styles.title}>{title}</Text>
          {description ? <Text style={styles.description}>{description}</Text> : null}
        </View>
        {onPress ? <Text style={styles.chevron}>›</Text> : null}
      </View>

      {children ? <View style={styles.children}>{children}</View> : null}

      {cta ? <Text style={styles.cta}>{cta}</Text> : null}
    </Pressable>
  );
}

/** Small row-level item used inside a CategoryCard to represent a specific calc link. */
export function ListRow({
  label,
  description,
  index,
  onPress,
}: {
  label: string;
  description?: string;
  index?: number;
  onPress?: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      style={({ pressed }) => [rowStyles.row, pressed && rowStyles.rowPressed]}
    >
      {typeof index === 'number' ? (
        <Text style={rowStyles.index}>{String(index + 1).padStart(2, '0')}</Text>
      ) : null}
      <View style={rowStyles.textCol}>
        <Text style={rowStyles.label}>{label}</Text>
        {description ? <Text style={rowStyles.description}>{description}</Text> : null}
      </View>
      <Ionicons name="chevron-forward" size={16} color={C.dim} style={rowStyles.arrow} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.sm + 4,
  },
  cardPressed: { opacity: 0.85 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  iconBox: {
    width: 40, height: 40,
    borderRadius: 10,
    borderWidth: 1, borderColor: C.edge,
    backgroundColor: C.panel2,
    alignItems: 'center', justifyContent: 'center',
    marginRight: SP.sm + 4,
  },
  emojiBox: {
    width: 40, height: 40,
    borderRadius: 10,
    backgroundColor: C.panel2,
    alignItems: 'center', justifyContent: 'center',
    marginRight: SP.sm + 4,
  },
  emoji: { fontSize: 20 },
  titleCol: { flex: 1 },
  title: {
    fontFamily: FONT.sansBold,
    fontSize: 16, color: C.ink,
    letterSpacing: -0.2,
  },
  description: {
    fontFamily: FONT.sans,
    fontSize: 13, color: C.ink2,
    marginTop: 2, lineHeight: 18,
  },
  chevron: {
    fontFamily: FONT.mono,
    fontSize: 22,
    color: C.dim,
    marginLeft: SP.sm,
  },
  children: { marginTop: SP.sm + 4 },
  cta: {
    fontFamily: FONT.sansMed,
    fontSize: 13, color: C.accent,
    marginTop: SP.sm + 4,
    letterSpacing: 0.3,
  },
});

const rowStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: SP.md,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    backgroundColor: C.panel,
    marginBottom: SP.sm,
  },
  rowPressed: { borderColor: '#31373D', backgroundColor: C.panel2 },
  index: {
    fontFamily: FONT.monoBold,
    fontSize: 11,
    color: C.accent,
    letterSpacing: 1.1,
    marginRight: SP.sm + 4,
    minWidth: 22,
  },
  textCol: { flex: 1 },
  label: {
    fontFamily: FONT.sansMed,
    fontSize: 15,
    color: C.ink,
    letterSpacing: -0.1,
  },
  description: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.ink2,
    lineHeight: 16,
    marginTop: 2,
  },
  arrow: {
    marginLeft: SP.sm,
  },
});
